import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { FaPlus, FaArrowRight, FaHeart, FaShieldAlt, FaGraduationCap, FaLeaf, FaHandsHelping } from "react-icons/fa";

// Sample Data (Apne icons, images, aur details ke according update kar sakte hain)
const INTERVENTIONS = [
  {
    id: "01",
    tab: "Health & Care",
    kicker: "Wellness First",
    title: "Healthcare Infrastructure & Medical Camps",
    text: "Delivering primary healthcare, preventive checkups, and critical medicine supply to remote rural regions.",
    points: [
      "Mobile Medical Units (MMUs) deployed",
      "Free preventive health checkups & eye screening",
      "Maternal & Child Health Care Support"
    ],
    metrics: [
      { v: "50K+", k: "Patients Screened" },
      { v: "120+", k: "Camps Organized" },
      { v: "15", k: "Districts Covered" }
    ],
    image: "https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?auto=format&fit=crop&w=1200&q=80",
    accent: "#DA2128",
    glow: "rgba(218, 33, 40, 0.4)"
  },
  {
    id: "02",
    tab: "Road Safety",
    kicker: "Safe Mobility",
    title: "National Road Safety & Awareness Drives",
    text: "Partnering with transport authorities and communities to reduce road accidents through education and safety gear distribution.",
    points: [
      "Driver health & eye checkup camps",
      "Helmets & reflective gear distribution",
      "First-responder training for highway accidents"
    ],
    metrics: [
      { v: "25K+", k: "Drivers Trained" },
      { v: "40%", k: "Accident Reduction" },
      { v: "80+", k: "Highways Covered" }
    ],
    image: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=1200&q=80",
    accent: "#DB6400",
    glow: "rgba(219, 100, 0, 0.4)"
  },
  {
    id: "03",
    tab: "Education",
    kicker: "Empowering Minds",
    title: "Digital Classrooms & Skill Development",
    text: "Upgrading government schools with smart boards, STEM labs, and career guidance programs for underserved youth.",
    points: [
      "Smart classroom setups with internet access",
      "Teacher training & digital pedagogy",
      "Scholarships for meritorious students"
    ],
    metrics: [
      { v: "100+", k: "Schools Upgraded" },
      { v: "18K+", k: "Students Impacted" },
      { v: "92%", k: "Pass Rate Boost" }
    ],
    image: "https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1200&q=80",
    accent: "#F59C00",
    glow: "rgba(245, 156, 0, 0.4)"
  },
  {
    id: "04",
    tab: "Environment",
    kicker: "Green Future",
    title: "Afforestation & Renewable Energy Initiatives",
    text: "Restoring eco-systems through mass tree plantations, solar light installations, and rain-water harvesting structures.",
    points: [
      "Miyawaki urban forest creation",
      "Solar street light installations in villages",
      "Water pond restoration and rejuvenation"
    ],
    metrics: [
      { v: "100K+", k: "Saplings Planted" },
      { v: "45", k: "Villages Solarized" },
      { v: "30+", k: "Ponds Rejuvenated" }
    ],
    image: "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?auto=format&fit=crop&w=1200&q=80",
    accent: "#10B981",
    glow: "rgba(16, 185, 129, 0.4)"
  },
  {
    id: "05",
    tab: "Inclusion",
    kicker: "Equal Opportunity",
    title: "Women Empowerment & Inclusive Livelihoods",
    text: "Vocational skill centers and micro-entrepreneurship support for women and persons with disabilities.",
    points: [
      "Self-Help Group (SHG) capacity building",
      "Tailoring & handicraft skill development",
      "Assistive device distribution for PwDs"
    ],
    metrics: [
      { v: "12K+", k: "Women Trained" },
      { v: "350+", k: "Micro-businesses" },
      { v: "85%", k: "Financial Independence" }
    ],
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=1200&q=80",
    accent: "#8B5CF6",
    glow: "rgba(139, 92, 246, 0.4)"
  }
];

// Icons map according to index
const ICONS = [FaHeart, FaShieldAlt, FaGraduationCap, FaLeaf, FaHandsHelping];

const SLIDE_MS = 7500;
const RING_R = 24;
const RING_DASH = 2 * Math.PI * RING_R;

export default function Interventions() {
  const [active, setActive] = useState(0);
  const [openMobile, setOpenMobile] = useState(0);
  const pausedRef = useRef(false);

  const progress = useMotionValue(0);
  const dashOffset = useTransform(progress, (p) => RING_DASH * (1 - p));
  const barWidth = useTransform(progress, (p) => `${p * 100}%`);

  useEffect(() => {
    let raf = 0;
    let last = null;
    let elapsed = 0;
    progress.set(0);

    const loop = (t) => {
      if (last === null) last = t;
      const dt = t - last;
      last = t;
      if (!pausedRef.current) elapsed += dt;
      const p = Math.min(elapsed / SLIDE_MS, 1);
      progress.set(p);
      if (p >= 1) {
        setActive((a) => (a + 1) % INTERVENTIONS.length);
        return;
      }
      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [active, progress]);

  const select = (i) => {
    setActive(i);
    progress.set(0);
  };

  const pause = () => {
    pausedRef.current = true;
  };
  const resume = () => {
    pausedRef.current = false;
  };

  const current = INTERVENTIONS[active];
  const CurrentIcon = ICONS[active] || FaHeart;

  return (
    <section
      id="interventions"
      className="grain relative overflow-hidden bg-[#FEF7F4] py-20 text-[#12100E] lg:py-28"
    >
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0">
        <motion.div
          key={`glow-${active}`}
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 0.25, scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="absolute -top-40 left-1/4 h-[38rem] w-[38rem] rounded-full blur-[130px]"
          style={{ background: current.glow }}
        />
        <div className="animate-glow absolute -bottom-52 -right-24 h-[34rem] w-[34rem] rounded-full bg-[#F59C00]/15 blur-[140px]" />
        <div className="blueprint absolute inset-0 opacity-[0.05]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 lg:px-10">
        {/* Heading */}
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-xl">
            <motion.h2
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.08 }}
              className="font-display mt-4 text-3xl font-medium leading-[1.05] tracking-tight text-[#12100E] md:text-4xl lg:text-[2.9rem]"
            >
              Key <span className="text-gradient-ember italic">Initiatives</span>
            </motion.h2>
            <p className="mt-3 max-w-md text-[13px] font-medium leading-relaxed text-[#12100E]/70">
              Tap a title to open the programme. Five focused tracks delivering measurable impact
              across India&apos;s social and environmental frontlines.
            </p>
          </div>

          <div className="flex items-center gap-5">
            <div className="text-right">
              <div className="font-display text-4xl leading-none text-[#12100E]">
                {String(active + 1).padStart(2, "0")}
              </div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#12100E]/50">
                of {String(INTERVENTIONS.length).padStart(2, "0")}
              </div>
            </div>
            <div className="relative h-14 w-14">
              <svg viewBox="0 0 56 56" className="h-full w-full -rotate-90">
                <circle
                  cx="28"
                  cy="28"
                  r={RING_R}
                  fill="none"
                  stroke="rgba(0,0,0,.12)"
                  strokeWidth="2"
                />
                <motion.circle
                  cx="28"
                  cy="28"
                  r={RING_R}
                  fill="none"
                  stroke={current.accent}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray={RING_DASH}
                  style={{ strokeDashoffset: dashOffset }}
                />
              </svg>
              <CurrentIcon
                className="absolute inset-0 m-auto h-5 w-5"
                style={{ color: current.accent }}
              />
            </div>
          </div>
        </div>

        {/* ---------- SWITCH BAR ---------- */}
        <div
          className="scrollbar-hide mt-10 flex gap-1.5 overflow-x-auto rounded-full border border-black/10 bg-black/[0.03] p-1.5 backdrop-blur-xl"
          onMouseEnter={pause}
          onMouseLeave={resume}
        >
          {INTERVENTIONS.map((item, i) => {
            const TabIcon = ICONS[i] || FaHeart;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => select(i)}
                className={`relative shrink-0 rounded-full px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] transition-colors duration-300 ${
                  active === i ? "text-[#12100E]" : "text-[#12100E]/60 hover:text-[#12100E]"
                }`}
              >
                {active === i && (
                  <motion.span
                    layoutId="afcsr-switch-pill"
                    transition={{ type: "spring", stiffness: 340, damping: 30 }}
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: `linear-gradient(120deg, ${item.accent}33, rgba(255,255,255,0.9))`,
                      boxShadow: `0 4px 18px -4px ${item.glow}`,
                    }}
                  />
                )}
                <span className="relative flex items-center gap-2">
                  <TabIcon className="h-3 w-3" />
                  {item.tab}
                </span>
              </button>
            );
          })}
        </div>

        {/* ---------- DESKTOP: expanding panels (DARK SPLIT CARDS) ---------- */}
        <div
          className="mt-6 hidden h-[32rem] gap-3.5 lg:flex"
          onMouseEnter={pause}
          onMouseLeave={resume}
        >
          {INTERVENTIONS.map((item, i) => {
            const isActive = active === i;
            const PanelIcon = ICONS[i] || FaHeart;
            return (
              <motion.div
                key={item.id}
                onClick={() => select(i)}
                initial={false}
                animate={{ flexGrow: isActive ? 7 : 1 }}
                transition={{ type: "spring", stiffness: 130, damping: 22 }}
                style={{ flexBasis: 0 }}
                className={`group relative min-w-[80px] cursor-pointer overflow-hidden rounded-[28px] border transition-all duration-500 ${
                  isActive 
                    ? "border-black/20 bg-[#12100E] shadow-2xl shadow-black/25" 
                    : "border-black/10 bg-[#FAF6F0] hover:border-black/20 shadow-md shadow-black/5"
                }`}
              >
                {/* COLLAPSED STATE (Side Rail) */}
                <AnimatePresence>
                  {!isActive && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="absolute inset-0 flex w-[80px] flex-col items-center justify-between py-6 z-10"
                    >
                      <span className="font-display text-sm font-bold text-[#12100E]/40">0{i + 1}</span>
                      <span className="vertical-rl rotate-180 whitespace-nowrap text-xs font-bold uppercase tracking-[0.28em] text-[#12100E]/80">
                        {item.tab}
                      </span>
                      <span
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white shadow-sm transition-transform duration-300 group-hover:scale-110"
                        style={{ color: item.accent }}
                      >
                        <FaPlus className="h-3 w-3" />
                      </span>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* EXPANDED STATE (Dark Split Layout - Text & Image Both Clear) */}
                <AnimatePresence>
                  {isActive && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.35, delay: 0.1 }}
                      className="relative flex h-full w-full overflow-hidden"
                    >
                      {/* LEFT HALF: Content Container */}
                      <div className="relative z-10 flex w-[55%] flex-col justify-between p-8 xl:p-9 bg-gradient-to-r from-[#12100E] via-[#12100E] to-[#12100E]/90 text-white">
                        <div>
                          {/* Top Badge & Number */}
                          <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.15 }}
                            className="flex items-center gap-3"
                          >
                            <span 
                              className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white shadow-sm"
                              style={{ background: item.accent }}
                            >
                              0{i + 1} — {item.kicker}
                            </span>
                          </motion.div>

                          {/* Title */}
                          <motion.h3
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="font-display mt-4 text-2xl xl:text-3xl font-semibold leading-[1.15] tracking-tight text-white"
                          >
                            {item.title}
                          </motion.h3>

                          {/* Description */}
                          <motion.p
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.25 }}
                            className="mt-3 text-xs xl:text-sm font-normal leading-relaxed text-white/80"
                          >
                            {item.text}
                          </motion.p>

                          {/* Bullet Points */}
                          <motion.ul
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="mt-4 grid gap-2"
                          >
                            {item.points.map((p) => (
                              <li key={p} className="flex items-center gap-2.5 text-xs font-medium text-white/90">
                                <span
                                  className="h-1.5 w-1.5 rounded-full shrink-0"
                                  style={{ background: item.accent }}
                                />
                                {p}
                              </li>
                            ))}
                          </motion.ul>
                        </div>

                        {/* Bottom Metrics Bar */}
                        <motion.div
                          initial={{ opacity: 0, y: 14 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.35 }}
                          className="mt-6 flex flex-wrap gap-2.5"
                        >
                          {item.metrics.map((m) => (
                            <div
                              key={m.k}
                              className="rounded-xl border border-white/10 bg-white/10 px-3.5 py-2 backdrop-blur-md"
                            >
                              <div className="font-display text-sm xl:text-base font-bold leading-none" style={{ color: item.accent }}>
                                {m.v}
                              </div>
                              <div className="mt-1 text-[9px] font-bold uppercase tracking-[0.12em] text-white/60">
                                {m.k}
                              </div>
                            </div>
                          ))}
                        </motion.div>
                      </div>

                      {/* RIGHT HALF: Crisp Image Showcase */}
                      <div className="relative w-[45%] h-full overflow-hidden">
                        <img
                          src={item.image}
                          alt={item.title}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-1000 ease-out hover:scale-105"
                        />
                        {/* Blend overlay */}
                        <div className="absolute inset-0 bg-gradient-to-r from-[#12100E] via-transparent to-transparent opacity-80" />
                        
                        {/* Floating Icon Badge */}
                        <div className="absolute top-6 right-6 flex h-12 w-12 items-center justify-center rounded-2xl border border-white/20 bg-black/40 backdrop-blur-md shadow-lg">
                          <PanelIcon className="h-6 w-6" style={{ color: item.accent }} />
                        </div>
                      </div>

                      {/* Bottom Progress Timer Bar */}
                      <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/10 z-20">
                        <motion.div
                          className="h-full"
                          style={{ width: barWidth, background: item.accent }}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* ---------- MOBILE / TABLET: accordion ---------- */}
        <div className="mt-6 space-y-3 lg:hidden">
          {INTERVENTIONS.map((item, i) => {
            const isOpen = openMobile === i;
            const RowIcon = ICONS[i] || FaHeart;
            return (
              <div
                key={item.id}
                className="overflow-hidden rounded-[28px] border border-black/10 bg-white/80 shadow-sm backdrop-blur-xl"
              >
                <button
                  type="button"
                  onClick={() => {
                    setOpenMobile(isOpen ? null : i);
                    select(i);
                  }}
                  className="flex w-full items-center gap-3 px-4 py-4 text-left"
                >
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white transition-colors"
                    style={{ background: isOpen ? item.accent : "rgba(0,0,0,.08)", color: isOpen ? "#fff" : "#12100E" }}
                  >
                    <RowIcon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[9px] font-bold uppercase tracking-[0.2em] text-[#12100E]/50">
                      0{i + 1} · {item.kicker}
                    </span>
                    <span className="font-display block truncate text-base font-semibold text-[#12100E]">
                      {item.title}
                    </span>
                  </span>
                  <motion.span
                    animate={{ rotate: isOpen ? 45 : 0 }}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-black/15 text-[#12100E]"
                  >
                    <FaPlus className="h-2.5 w-2.5" />
                  </motion.span>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <div className="px-4 pb-5">
                        <div className="relative h-44 overflow-hidden rounded-2xl">
                          <img
                            src={item.image}
                            alt={item.title}
                            className="h-full w-full object-cover"
                          />
                          <div
                            className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"
                          />
                        </div>
                        <p className="mt-3 text-[13px] font-medium leading-relaxed text-[#12100E]/80">
                          {item.text}
                        </p>
                        <ul className="mt-3 grid gap-1.5">
                          {item.points.map((p) => (
                            <li
                              key={p}
                              className="flex items-center gap-2 text-[12px] font-semibold text-[#12100E]/75"
                            >
                              <span
                                className="h-1.5 w-1.5 rounded-full"
                                style={{ background: item.accent }}
                              />
                              {p}
                            </li>
                          ))}
                        </ul>
                        <div className="mt-4 grid grid-cols-3 gap-2">
                          {item.metrics.map((m) => (
                            <div
                              key={m.k}
                              className="rounded-2xl border border-black/10 bg-white px-2.5 py-2 text-center shadow-xs"
                            >
                              <div className="font-display text-sm font-bold" style={{ color: item.accent }}>{m.v}</div>
                              <div className="mt-0.5 text-[8.5px] font-bold uppercase tracking-[0.1em] text-[#12100E]/60">
                                {m.k}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Footer info bar */}
        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-black/10 pt-6">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#12100E]/50">
            Auto-rotating · hover to pause
          </p>
          {/* <a
            href="#impact"
            className="group inline-flex items-center gap-2 rounded-full border border-black/15 bg-black/5 px-5 py-2.5 text-xs font-bold text-[#12100E] backdrop-blur transition-colors hover:bg-black/10"
          >
            See the measured impact
            <FaArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
          </a> */}
        </div>
      </div>
    </section>
  );
}